import { useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import CardJourneyView from './CardJourneyView';
import { useLearningPack } from '@/hooks/useLearningPack';
import { userActivitiesAPI } from '@/api/userActivities';
import { usePoints } from '@/context/PointsContext';

export default function LearningPackViewer({ 
  isOpen, 
  onClose, 
  activities = [], 
  chapterId = null,
  embedded = false,
  onXPEarned = null,
  currentIndex = undefined,
  onIndexChange = undefined,
}) {
  const { refreshPoints } = usePoints();
  
  // Use chapterId as packId for completion tracking
  const packId = chapterId ? `chapter_${chapterId}` : 'default';
  const packState = useLearningPack(packId);
  
  // Load activity completions from backend when modal opens and chapterId is available
  useEffect(() => {
    if (isOpen && chapterId) {
      loadCompletionsFromBackend();
    }
  }, [isOpen, chapterId]);
  
  // Load completions from backend and sync with localStorage
  const loadCompletionsFromBackend = async () => {
    if (!chapterId) return;

    try {
      const completions = await userActivitiesAPI.getChapterCompletions(chapterId);
      console.log('📊 Loaded activity completions from backend:', completions);
      
      // Sync backend completions with localStorage
      // Create a map of annotation_id -> completion data
      const backendCompletions = {};
      completions.forEach(completion => {
        const annotationId = completion.annotation_id || completion.annotation?.id;
        if (annotationId) {
          backendCompletions[String(annotationId)] = {
            completedAt: completion.completed_at || new Date().toISOString(),
            xpEarned: completion.xp_earned || 0,
            gemsEarned: completion.gems_earned || 0
          };
        }
      });
      
      // Update packState with backend data
      // Merge backend completions with existing localStorage data
      // Backend is the source of truth, so we prioritize backend data
      const storageKey = `learningPack_${packId}_progress`;
      const existingLocalData = JSON.parse(localStorage.getItem(storageKey) || '{}');
      
      // Merge: backend data takes priority, but keep any local-only completions
      const mergedCompletions = {
        ...existingLocalData,
        ...backendCompletions // Backend data overwrites local data
      };
      
      // Save merged data to localStorage
      if (Object.keys(mergedCompletions).length > 0) {
        localStorage.setItem(storageKey, JSON.stringify(mergedCompletions));
        console.log('💾 Saved merged completions to localStorage:', mergedCompletions);
      }
      
      // Force reload from localStorage to update React state
      packState.reloadFromStorage();
      
      console.log('✅ Synced backend completions with localStorage');
    } catch (error) {
      console.error('Error loading activity completions from backend:', error);
      // Don't block the UI if backend is unavailable
    }
  };

  const handleComplete = async (activityId, xpReward) => {
    const activity = (activities || []).find((a) => String(a.id) === String(activityId));
    const annotationId = activity?.annotation_id || activity?.id;

    // Optimistic UI: mark complete immediately so right-panel checkmarks update instantly.
    // Also trigger the existing global celebration animation/sound via the same event used by ActivityModal.
    const rawType = activity?.type || activity?.annotation_type || null;
    const normalizedType = rawType
      ? String(rawType).trim().charAt(0).toUpperCase() + String(rawType).trim().slice(1).toLowerCase()
      : null;

    const optimisticXP = xpReward ?? 0;
    const optimisticEarned = packState.completeItem(activityId, optimisticXP);
    if (optimisticEarned > 0 && normalizedType) {
      window.dispatchEvent(
        new CustomEvent('activity-completed', {
          detail: {
            activityId,
            isAlreadyCompleted: false,
            pointsEarned: optimisticXP,
            gemsEarned: 0,
            activityType: normalizedType,
          },
        })
      );
    }

    try {
      if (chapterId && annotationId) {
        const completionData = await userActivitiesAPI.completeActivity(annotationId, chapterId, {});
        const earnedXP = completionData?.xp_earned ?? xpReward ?? 0;
        const earnedGems = completionData?.gems_earned ?? 0;

        // Local completion already applied optimistically; re-sync from backend shortly after.
        // (This overwrites any optimistic reward amounts in localStorage.)
        packState.reloadFromStorage();

        // Refresh points (XP/Gems) in the header
        if (refreshPoints) {
          await refreshPoints();
        }

        if (onXPEarned && (earnedXP > 0 || earnedGems > 0)) {
          onXPEarned(earnedXP + earnedGems);
        }

        // Re-sync from backend shortly after (handles race conditions)
        setTimeout(() => {
          if (chapterId) loadCompletionsFromBackend();
        }, 750);

        return;
      }

      // Fallback: local-only
      if (onXPEarned && optimisticEarned > 0) onXPEarned(optimisticEarned);
    } catch (e) {
      // Fallback: local-only (don’t block completion UI)
      if (onXPEarned && optimisticEarned > 0) onXPEarned(optimisticEarned);
    }
  };

  if (!isOpen || activities.length === 0) return null;

  if (embedded) {
    return (
      <div className="w-full h-full bg-[hsl(var(--main-bg))]">
        <CardJourneyView
          items={activities}
          onExit={onClose}
          onComplete={handleComplete}
          isItemCompleted={packState.isItemCompleted}
          currentIndex={currentIndex}
          onIndexChange={onIndexChange}
        />
      </div>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))]">
        <CardJourneyView
          items={activities}
          onExit={onClose}
          onComplete={handleComplete}
          isItemCompleted={packState.isItemCompleted}
          currentIndex={currentIndex}
          onIndexChange={onIndexChange}
        />
      </DialogContent>
    </Dialog>
  );
}
